/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { FC, useCallback, useMemo, useRef, useState } from "react";
import {
	ISchemaConstMapModel,
	ISchemaConstMapModelEntry,
	SCHEMA_VALIDATION_ERROR_TYPE,
	TGenericBlueprintSchema,
	TGenericModelNode,
	ValidatorString
} from "@adapptio/lib-blueprint";
import { IInspectorControls, TInspectorControlComponent } from "../InspectorTypes";
import { useBlueprintModel } from "../../../hooks/useBlueprintModel";
import { InvalidModelNode } from "../InvalidModelNode";
import { FieldRow, FIELD_ROW_TYPE } from "../../Fields/FieldRow";
import { FieldListItem, IFieldListItemAction } from "../../Fields/FieldListItem";
import { InspectorFloatingPanel } from "../InspectorFloatingPanel";
import { InspectModel } from "../InspectModel";
import { FieldButton } from "../../Fields/FieldButton";
import { useFieldState } from "../../../hooks/useFieldGroup";
import { FieldText } from "../../Fields/FieldText";
import { FieldGroup } from "../../Fields/FieldGroup";

export const ControlConstMap = (model: TGenericModelNode): IInspectorControls => {
	return {
		children: {
			component: ControlConstMap_Children,
			model: model
		}
	};
};

export const ControlConstMap_Children: TInspectorControlComponent<
	ISchemaConstMapModel<TGenericBlueprintSchema>
> = ({ model: _model, depth, path }) => {
	const { model } = useBlueprintModel(_model, "constMap");

	const handleAdd = useCallback(
		(key: string) => {
			model.schema.addElement(model, key, undefined, true);
		},
		[ model ]
	);

	const handleRemove = useCallback(
		(index: number) => {
			if (!model) {
				return;
			}

			model.schema.removeElement(model, index, true);
		},
		[ model ]
	);

	if (!model) {
		return <InvalidModelNode model={_model} />;
	}

	return (
		<>
			{model.items.length > 0 ? (
				<FieldRow type={FIELD_ROW_TYPE.LIST}>
					{model.items.map((item, index) => {
						return (
							<ControlConstMap_ChildItem
								key={item.value.nodeId}
								entry={item}
								depth={depth}
								path={path.concat([ "item" ])}
								index={index}
								onRemove={handleRemove}
							/>
						);
					})}
				</FieldRow>
			) : null}
			<ControlConstMap_AddItem model={model} onAdd={handleAdd} />
		</>
	);
};

interface IControlConstMap_ChildItemProps {
	entry: ISchemaConstMapModelEntry<TGenericBlueprintSchema>;
	depth: number;
	path: string[];
	index: number;
	onRemove: (index: number) => void;
}

const ControlConstMap_ChildItem: FC<IControlConstMap_ChildItemProps> = ({ entry, index, path, onRemove }) => {
	useBlueprintModel(entry.key);
	useBlueprintModel(entry.value);

	const [ isPanelOpened, setIsPanelOpened ] = useState(false);
	const itemElRef = useRef<HTMLDivElement>();
	const itemClicked = useRef(false);

	const itemActions = useMemo(
		() => [
			{
				icon: "mdi/delete",
				tooltip: "Remove item",
				onClick: () => {
					if (isPanelOpened) {
						setIsPanelOpened(false);
						setTimeout(() => onRemove(index), 200);
					} else {
						onRemove(index);
					}
				}
			} as IFieldListItemAction
		],
		[ index, isPanelOpened, onRemove ]
	);

	const renderPanel = useCallback(() => {
		return (
			<>
				<InspectModel model={entry.key} depth={0} path={path.concat([ "key" ])} />
				<InspectModel model={entry.value} depth={0} path={path.concat([ "value" ])} />
			</>
		);
	}, [ entry.key, entry.value ]);

	const togglePanel = useCallback(() => {
		setIsPanelOpened((x) => !x);
		itemClicked.current = true;
		setTimeout(() => (itemClicked.current = false), 1);
	}, []);

	const closePanel = useCallback(() => {
		setIsPanelOpened(false);
	}, [ setIsPanelOpened ]);

	return (
		<FieldListItem
			label={entry.key.value}
			actions={itemActions}
			isActive={isPanelOpened}
			onClick={togglePanel}
			ref={itemElRef}
		>
			<InspectorFloatingPanel
				for={itemElRef}
				isOpened={isPanelOpened}
				render={renderPanel}
				onClose={closePanel}
			/>
		</FieldListItem>
	);
};

interface ControlConstMap_AddItemProps {
	model: ISchemaConstMapModel<TGenericBlueprintSchema>;
	onAdd: (key: string) => void;
}

const ControlConstMap_AddItem: FC<ControlConstMap_AddItemProps> = ({ model, onAdd }) => {
	const { updateRef } = useBlueprintModel(model);

	const [ fieldState, setFieldState ] = useFieldState(
		{
			key: {
				defaultValue: "",
				validators: [
					ValidatorString({ required: true, min: 1 }),
					(value: string) => {
						const existingKeys = model.items.map((item) => item.key.value);

						if (existingKeys.includes(value)) {
							return [
								{
									type: SCHEMA_VALIDATION_ERROR_TYPE.DUPLICATE_IDENTIFIER,
									// @todo Translate
									message: "This key already exists.",
									metaData: {}
								}
							];
						} else {
							return [];
						}
					}
				]
			}
		},
		[ model, updateRef ]
	);

	const handleAdd = useCallback(() => {
		onAdd(fieldState.key.value);
		setFieldState.key("");
	}, [ onAdd, fieldState.key.value ]);

	return (
		<FieldRow type={FIELD_ROW_TYPE.LIST}>
			<FieldGroup>
				<FieldText
					value={fieldState.key.value}
					placeholder={model.schema.opts.keyOpts?.placeholder}
					validationErrors={fieldState.key.validationErrors}
					onChange={setFieldState.key}
					onPressEnter={handleAdd}
					grow={true}
				/>
				<FieldButton
					icon="mdi/plus"
					// @todo Translate
					tooltip="Add Item"
					onClick={handleAdd}
				/>
			</FieldGroup>
		</FieldRow>
	);
};
